<script setup lang="ts">
import { NPopconfirm } from 'naive-ui'
import type { ListItem } from '../stores/lists'

// One tickable item on the selected list: drag handle, done checkbox, the
// title (struck through and faint once done) with its link / notes /
// priority underneath, then reorder, edit and remove controls.
defineProps<{
  item: ListItem
  isEditing: boolean
  draggingId: number | null
  dragOverId: number | null
  isFirst: boolean
  isLast: boolean
}>()

const emit = defineEmits<{
  (e: 'toggle', id: number, done: boolean): void
  (e: 'edit', item: ListItem): void
  (e: 'delete', id: number): void
  (e: 'dragstart', event: DragEvent, id: number): void
  (e: 'dragover', event: DragEvent, id: number): void
  (e: 'dragend'): void
  (e: 'drop', event: DragEvent, id: number): void
  (e: 'move-up', id: number): void
  (e: 'move-down', id: number): void
}>()

const PRIORITY_LABELS: Record<number, string> = { 1: 'High', 2: 'Medium', 3: 'Low' }
const PRIORITY_CLASSES: Record<number, string> = { 1: 'text-status-alert', 2: 'text-ink-muted' }

function priorityLabel(p: number | null) {
  return p ? (PRIORITY_LABELS[p] ?? null) : null
}

function priorityClass(p: number | null) {
  return (p != null && PRIORITY_CLASSES[p]) || 'text-ink-faint'
}

function onChange(event: Event, id: number): void {
  emit('toggle', id, (event.target as HTMLInputElement).checked)
}

const ICON_BUTTON_CLASS =
  'flex h-11 w-11 items-center justify-center rounded-lg text-ink-muted hover:bg-surface-hover hover:text-ink disabled:cursor-not-allowed disabled:opacity-40 sm:h-8 sm:w-8'
const TEXT_BUTTON_CLASS =
  'min-h-11 rounded-lg px-2 text-xs text-ink-muted hover:bg-surface-hover hover:text-ink sm:min-h-8'
</script>

<template>
  <div
    draggable="true"
    class="flex cursor-grab items-center gap-2 border-t border-border-divider py-1 transition-transform active:cursor-grabbing"
    :class="{
      'rounded-lg bg-surface-hover': isEditing,
      'rounded-lg bg-life/10 ring-1 ring-life/60': dragOverId === item.id,
      'opacity-30 scale-95': draggingId === item.id,
    }"
    data-testid="list-item-row"
    @dragstart="emit('dragstart', $event, item.id)"
    @dragover="emit('dragover', $event, item.id)"
    @dragend="emit('dragend')"
    @drop="emit('drop', $event, item.id)"
  >
    <span
      class="max-sm:hidden shrink-0 select-none text-lg leading-tight text-ink-faint hover:text-ink-muted"
      role="button"
      tabindex="0"
      :aria-label="`Drag to reorder: ${item.title}`"
      >⠿</span
    >
    <label class="flex min-h-11 min-w-0 flex-1 cursor-pointer items-center gap-3">
      <input
        type="checkbox"
        class="m-0 h-[18px] w-[18px] shrink-0 cursor-pointer accent-life"
        :checked="item.done"
        :aria-label="`Mark ${item.title} as done`"
        @change="onChange($event, item.id)"
      />
      <span class="flex min-w-0 flex-1 flex-col gap-0.5 py-1.5">
        <span
          class="break-words"
          :class="item.done ? 'text-ink-faint line-through' : 'text-ink'"
          data-testid="list-item-title"
          >{{ item.title }}</span
        >
        <a
          v-if="item.url"
          :href="item.url"
          target="_blank"
          rel="noopener"
          class="truncate text-xs text-life hover:underline"
          @click.stop
          >{{ item.url }}</a
        >
        <span v-if="item.notes" class="text-xs text-ink-faint">{{ item.notes }}</span>
        <span
          v-if="item.priority"
          class="text-xs"
          :class="priorityClass(item.priority)"
          data-testid="list-item-priority"
          >Priority: {{ priorityLabel(item.priority) }}</span
        >
      </span>
    </label>
    <button
      type="button"
      :class="ICON_BUTTON_CLASS"
      :disabled="isFirst"
      :aria-label="`Move ${item.title} up`"
      @click="emit('move-up', item.id)"
    >
      ↑
    </button>
    <button
      type="button"
      :class="ICON_BUTTON_CLASS"
      :disabled="isLast"
      :aria-label="`Move ${item.title} down`"
      @click="emit('move-down', item.id)"
    >
      ↓
    </button>
    <button type="button" :class="TEXT_BUTTON_CLASS" :aria-label="`Edit ${item.title}`" @click="emit('edit', item)">
      Edit
    </button>
    <NPopconfirm positive-text="Remove" negative-text="Cancel" @positive-click="emit('delete', item.id)">
      <template #trigger>
        <button type="button" :class="TEXT_BUTTON_CLASS" :aria-label="`Delete ${item.title}`">Delete</button>
      </template>
      Remove "{{ item.title }}" from {{ item.list }}?
    </NPopconfirm>
  </div>
</template>
